import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, of, switchMap } from 'rxjs';

import { ProjectAddonService } from '../../services/project-addon.service';
import { ProjectAddonModel } from '../models/project-addon.model';
import { ProjectAddonActions } from '../state/project-addon/project-addon.actions';

export const loadAddonTypes = createEffect(
  (
    actions$ = inject(Actions),
    projectAddonService = inject(ProjectAddonService),
  ) => {
    return actions$.pipe(
      ofType(ProjectAddonActions.loadAddonTypes),
      switchMap(() =>
        projectAddonService.getAddonTypes().pipe(
          map((addonTypes) =>
            ProjectAddonActions.loadAddonTypesSuccess({ addonTypes }),
          ),
          catchError(() => of(ProjectAddonActions.loadAddonTypesFail())),
        ),
      ),
    );
  },
  { functional: true },
);

export const loadProjectPlanAddons = createEffect(
  (
    actions$ = inject(Actions),
    projectAddonService = inject(ProjectAddonService),
  ) => {
    return actions$.pipe(
      ofType(ProjectAddonActions.loadProjectPlanAddons),
      switchMap(({ projectId }) =>
        projectAddonService.findAllByProjectId(projectId, {
          target: ProjectAddonModel.AddonTarget.Plan,
        }).pipe(
          map((addons) =>
            ProjectAddonActions.loadProjectPlanAddonsSuccess({ addons }),
          ),
          catchError(() => of(ProjectAddonActions.loadProjectPlanAddonsFail())),
        ),
      ),
    );
  },
  { functional: true },
);

export const loadProjectCameraAddons = createEffect(
  (
    actions$ = inject(Actions),
    projectAddonService = inject(ProjectAddonService),
  ) => {
    return actions$.pipe(
      ofType(ProjectAddonActions.loadProjectCameraAddons),
      switchMap(({ projectId }) =>
        projectAddonService.findAllByProjectId(projectId, {
          target: ProjectAddonModel.AddonTarget.Camera,
        }).pipe(
          map((addons) =>
            ProjectAddonActions.loadProjectCameraAddonsSuccess({ addons }),
          ),
          catchError(() => of(ProjectAddonActions.loadProjectCameraAddonsFail())),
        ),
      ),
    );
  },
  { functional: true },
);

export const createProjectAddon = createEffect(
  (
    actions$ = inject(Actions),
    projectAddonService = inject(ProjectAddonService),
  ) => {
    return actions$.pipe(
      ofType(ProjectAddonActions.createProjectAddon),
      switchMap(({ projectId, locationId, addon, target }) =>
        projectAddonService.create(projectId, locationId, addon).pipe(
          map((createdAddon) =>
            ProjectAddonActions.createProjectAddonSuccess({
              addon: createdAddon,
            }),
          ),
          catchError(() =>
            of(
              ProjectAddonActions.createProjectAddonFail({
                target,
              }),
            ),
          ),
        ),
      ),
    );
  },
  { functional: true },
);

export const updateProjectAddon = createEffect(
  (
    actions$ = inject(Actions),
    projectAddonService = inject(ProjectAddonService),
  ) => {
    return actions$.pipe(
      ofType(ProjectAddonActions.updateProjectAddon),
      switchMap(({ addonId, addon, target }) =>
        projectAddonService.update(addonId, addon).pipe(
          map((updatedAddon) =>
            ProjectAddonActions.updateProjectAddonSuccess({
              addon: updatedAddon,
            }),
          ),
          catchError(() =>
            of(
              ProjectAddonActions.updateProjectAddonFail({
                target,
              }),
            ),
          ),
        ),
      ),
    );
  },
  { functional: true },
);

export const bulkCreateCameraAddons = createEffect(
  (
    actions$ = inject(Actions),
    projectAddonService = inject(ProjectAddonService),
  ) => {
    return actions$.pipe(
      ofType(ProjectAddonActions.bulkCreateCameraAddons),
      switchMap(({ projectId, addons }) =>
        projectAddonService.bulkCreate(projectId, addons).pipe(
          map((createdAddons) =>
            ProjectAddonActions.bulkCreateCameraAddonsSuccess({
              addons: createdAddons,
            }),
          ),
          catchError(() =>
            of(
              ProjectAddonActions.bulkCreateCameraAddonsFail(),
            ),
          ),
        ),
      ),
    );
  },
  { functional: true },
);

export const bulkUpdateCameraAddons = createEffect(
  (
    actions$ = inject(Actions),
    projectAddonService = inject(ProjectAddonService),
  ) => {
    return actions$.pipe(
      ofType(ProjectAddonActions.bulkUpdateCameraAddons),
      switchMap(({ projectId, addons }) =>
        projectAddonService.bulkUpdate(projectId, addons).pipe(
          map((updatedAddons) =>
            ProjectAddonActions.bulkUpdateCameraAddonsSuccess({
              addons: updatedAddons,
            }),
          ),
          catchError(() =>
            of(
              ProjectAddonActions.bulkUpdateCameraAddonsFail(),
            ),
          ),
        ),
      ),
    );
  },
  { functional: true },
);

export const bulkUpdatePlanAddons = createEffect(
  (
    actions$ = inject(Actions),
    projectAddonService = inject(ProjectAddonService),
  ) => {
    return actions$.pipe(
      ofType(ProjectAddonActions.bulkUpdatePlanAddons),
      switchMap(({ projectId, addons }) =>
        projectAddonService.bulkUpdate(projectId, addons).pipe(
          map((updatedAddons) =>
            ProjectAddonActions.bulkUpdatePlanAddonsSuccess({
              addons: updatedAddons,
            }),
          ),
          catchError(() =>
            of(
              ProjectAddonActions.bulkUpdatePlanAddonsFail(),
            ),
          ),
        ),
      ),
    );
  },
  { functional: true },
);

export const bulkDeleteCameraAddons = createEffect(
  (
    actions$ = inject(Actions),
    projectAddonService = inject(ProjectAddonService),
  ) => {
    return actions$.pipe(
      ofType(ProjectAddonActions.bulkDeleteCameraAddons),
      switchMap(({ projectId, addonIds }) =>
        projectAddonService.bulkDelete(projectId, addonIds).pipe(
          map(() =>
            ProjectAddonActions.bulkDeleteCameraAddonsSuccess({
              addonIds,
            }),
          ),
          catchError(() =>
            of(ProjectAddonActions.bulkDeleteCameraAddonsFail()),
          ),
        ),
      ),
    );
  },
  { functional: true },
);

export const bulkDeletePlanAddons = createEffect(
  (
    actions$ = inject(Actions),
    projectAddonService = inject(ProjectAddonService),
  ) => {
    return actions$.pipe(
      ofType(ProjectAddonActions.bulkDeletePlanAddons),
      switchMap(({ projectId, addonIds }) =>
        projectAddonService.bulkDelete(projectId, addonIds).pipe(
          map(() =>
            ProjectAddonActions.bulkDeletePlanAddonsSuccess({
              addonIds,
            }),
          ),
          catchError(() =>
            of(ProjectAddonActions.bulkDeletePlanAddonsFail()),
          ),
        ),
      ),
    );
  },
  { functional: true },
);